import React, { useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, gridClasses, GridColDef } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { HgProduct } from "../../../../../../types/taxonomy";
import { FAI_LOCATION_QUERY } from "../../../../../../legacy/queries/FAI";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";
import { useQuery } from "@apollo/client";
import { LightTooltip } from "../../../../../../components/LightTooltip";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";
import { PieChartIcon } from "../../../../../../components/icons/FAI/PieChartIcon";
import { DecisionMakerIcon } from "../../../../../../components/icons/FAI/DecisionMakerIcon";
import { InfluencerIcon } from "../../../../../../components/icons/FAI/InfluencerIcon";
import FAIByLocationData from "../../../../../../legacy/queries/Installs";

interface FaiByLocationModalProps {
  open: boolean;
  onClose: () => void;
  product: Pick<HgProduct, "id" | "name">;
  department?: string;
  role?: string;
}

type SkeletonRow = { id: string; isSkeleton: true };
type DataRow = FAIByLocationData & { id: string; isSkeleton: false };
type TableRow = DataRow | SkeletonRow;

interface LocationQueryPayload {
  functionalIntelligenceByLocation: {
    data: FAIByLocationData[];
    count: number;
  };
}

const HeaderWithTooltip: React.FC<{
  title: string;
  tooltip?: string | React.ReactNode;
}> = React.memo(({ title, tooltip }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="subtitle2" fontWeight="bold">
        {title}
      </Typography>
      {tooltip && (
        <LightTooltip
          title={<Typography variant="body2">{tooltip}</Typography>}
          placement="top"
        >
          <IconButton size="small" sx={{ p: 0 }}>
            <HelpOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          </IconButton>
        </LightTooltip>
      )}
    </Stack>
  );
});

HeaderWithTooltip.displayName = "HeaderWithTooltip";

const BuyerRoleCell: React.FC<{
  decisionMaker: boolean;
  influencer: boolean;
}> = React.memo(({ decisionMaker, influencer }) => {
  if (!decisionMaker && !influencer) {
    return (
      <Typography
        variant="body2"
        sx={{ fontStyle: "italic", color: "text.secondary" }}
      >
        No buyer role detected
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {decisionMaker && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <DecisionMakerIcon width={16} height={16} />
          <Typography variant="body2">Decision Maker</Typography>
        </Stack>
      )}
      {influencer && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <InfluencerIcon width={16} height={14} />
          <Typography variant="body2">Influencer</Typography>
        </Stack>
      )}
    </Stack>
  );
});

BuyerRoleCell.displayName = "BuyerRoleCell";

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const FaiByLocationModal: React.FC<FaiByLocationModalProps> = ({
  open,
  onClose,
  product,
  department,
  role,
}) => {
  const company = useCompanyPageStore((state) => state.company);

  const { data, loading } = useQuery<LocationQueryPayload>(FAI_LOCATION_QUERY, {
    variables: {
      hgId: company!.hgId,
      productId: product.id,
      department: department?.toUpperCase().replace(/\s+/g, "_"),
      functionalAreaRole: role?.toUpperCase().replace(/\s+/g, "_"),
      per: 10,
    },
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
    },
    skip: !company || !product.id || !department,
  });

  const locationData = useMemo(
    () => data?.functionalIntelligenceByLocation?.data || [],
    [data?.functionalIntelligenceByLocation?.data]
  );
  const totalCount = useMemo(
    () => data?.functionalIntelligenceByLocation?.count || 0,
    [data?.functionalIntelligenceByLocation?.count]
  );
  const totalLocationCount = useMemo(
    () => locationData[0]?.totalLocationCount || 0,
    [locationData]
  );

  const rows: TableRow[] = useMemo(() => {
    if (loading) {
      return Array.from({ length: 10 }).map(
        (_, index) =>
          ({
            id: `skeleton-${index}`,
            isSkeleton: true,
          }) as SkeletonRow
      );
    }
    return locationData.map(
      (item: FAIByLocationData, index: number) =>
        ({
          ...item,
          id: `location-${index}`,
          isSkeleton: false,
        }) as DataRow
    );
  }, [locationData, loading]);

  const columnsDefinition: GridColDef<TableRow>[] = useMemo(
    () => [
      {
        field: "signalStrength",
        headerName: "Signal Share",
        width: 150,
        sortable: false,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Signal Share"
            tooltip="Signal Share is the portion of the signal that is coming from this location, presented as a percentage"
          />
        ),
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={32} height={32} />
                <Skeleton variant="text" width="40px" />
              </Stack>
            );
          }
          const rowData = params.row as DataRow;
          const percentage = parseFloat(rowData.signalStrength);

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <PieChartIcon percentage={percentage} size={32} />
              <Typography variant="body2" fontWeight="medium">
                {percentage.toFixed(2)}%
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: "buyerRole",
        headerName: "Buyer Role",
        flex: 1,
        width: 200,
        sortable: false,
        renderHeader: () => (
          <HeaderWithTooltip
            title="Buyer Role"
            tooltip={
              <div>
                <p>
                  Buyer Role refers to individuals who hold positions in
                  decision-making processes and possess the power to influence
                  critical choices within a company.
                </p>
                <p>
                  <b>Decision Makers:</b> These individuals occupy high-level
                  positions such as C-Level/VP roles, Directors, Department
                  Heads, or Company Founders.
                </p>
                <p>
                  <b>Influencers:</b> These individuals have influence within
                  their specific domains. They can include Associate VP roles,
                  Managers, Team Leads, or HR Business Partners.
                </p>
              </div>
            }
          />
        ),
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="80%" />;
          }
          const rowData = params.row as DataRow;
          return (
            <BuyerRoleCell
              decisionMaker={rowData.decisionMaker}
              influencer={rowData.influencer}
            />
          );
        },
      },
      {
        field: "country",
        headerName: "Country",
        width: 100,
        sortable: false,
        renderHeader: () => <HeaderWithTooltip title="Country" />,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="60%" />;
          }
          const rowData = params.row as DataRow;
          return (
            <Typography variant="body2" noWrap>
              {rowData.country}
            </Typography>
          );
        },
      },
      {
        field: "state",
        headerName: "State",
        width: 120,
        sortable: false,
        renderHeader: () => <HeaderWithTooltip title="State" />,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="70%" />;
          }
          const rowData = params.row as DataRow;
          const stateValue = rowData.state || "Unspecified";
          return (
            <Typography variant="body2" noWrap>
              {stateValue}
            </Typography>
          );
        },
      },
      {
        field: "city",
        headerName: "City",
        width: 150,
        sortable: false,
        renderHeader: () => <HeaderWithTooltip title="City" />,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="80%" />;
          }
          const rowData = params.row as DataRow;
          const cityValue = rowData.city || "Unspecified";
          return (
            <Tooltip title={cityValue} placement="bottom-start">
              <Typography variant="body2" noWrap>
                {cityValue}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "lastVerified",
        headerName: "Last Verified",
        width: 130,
        sortable: false,
        renderHeader: () => <HeaderWithTooltip title="Last Verified" />,
        renderCell: (params) => {
          if (params.row.isSkeleton) {
            return <Skeleton variant="text" width="90%" />;
          }
          const rowData = params.row as DataRow;
          return (
            <Typography variant="body2" noWrap>
              {formatDate(rowData.lastVerified.toString())}
            </Typography>
          );
        },
      },
    ],
    []
  );

  const titleText = useMemo(() => {
    if (!department || !product.name) return "";

    if (role) {
      return `${totalLocationCount} ${role} role locations for ${product.name} found`;
    }

    return `${totalLocationCount} ${department} department locations for ${product.name} found`;
  }, [department, product.name, totalLocationCount, role]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4" fontWeight="bold">
            Functional Area Intelligence by Location
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {titleText && <Typography variant="h5">{titleText}</Typography>}

        <Box sx={{ height: 560, width: "100%", marginLeft: -1 }}>
          <DataGrid
            rows={rows}
            columns={columnsDefinition}
            hideFooter
            rowHeight={50}
            getRowId={(row) => row.id}
            disableColumnFilter
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnResize
            disableColumnSelector
            sx={{
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                {
                  outline: "none",
                },
              [`& .${gridClasses.columnSeparator}`]: {
                [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
                  display: "none",
                },
              },
              "& .MuiDataGrid-columnHeader": {
                borderBottom: "none !important",
              },
              "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                "& .MuiIconButton-root": {
                  padding: 0,
                },
                "& .MuiTouchRipple-root": {
                  display: "none",
                },
              },
              "&, [class^=MuiDataGrid]": { border: "none" },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-filler": {
                display: "none",
              },
              ".MuiDataGrid-scrollbarFiller": {
                display: "none",
              },
              "& .MuiDataGrid-scrollbar--vertical": {
                ...lightScrollbarStyle,
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              },
            }}
          />
        </Box>

        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing Top {totalCount} of {totalLocationCount} results
          </Typography>
          <LightTooltip
            title={
              <Typography variant="body2">
                This table displays the top 5 locations by Signal Strength along
                with the next top 5 locations with a Buyer Role present.
              </Typography>
            }
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <HelpOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            </IconButton>
          </LightTooltip>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
